import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Detection, DetectionStatus, ScraperStatus, AdminLocationInfo,
  getDetections, updateDetectionStatus, triggerScraper, getScraperStatus,
} from '../api/adminApi';
import { CountryOverview, useAllCountries } from '../hooks/useAllCountries';
import ConfirmDetectionModal from '../components/ConfirmDetectionModal';
import styles from './DetectionsPage.module.css';

const MONTH_NAMES: Record<number, string> = {
  1: 'Muharram', 2: 'Safar', 3: "Rabi' al-Awwal", 4: "Rabi' al-Thani",
  5: "Jumada al-Awwal", 6: "Jumada al-Thani", 7: 'Rajab', 8: "Sha'ban",
  9: 'Ramadan', 10: 'Shawwal', 11: "Dhu al-Qi'dah", 12: 'Dhu al-Hijjah',
};

const CONFIDENCE_RANK: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 };

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function SignalIcon({ confidence }: { confidence: string }) {
  const high = confidence === 'HIGH';
  const med = confidence === 'HIGH' || confidence === 'MEDIUM';
  const color = confidence === 'HIGH' ? '#16a34a' : confidence === 'MEDIUM' ? '#d97706' : '#dc2626';
  const dim = '#e2e8f0';
  return (
    <svg viewBox="0 0 18 14" width="18" height="14" className={styles.signal}>
      <rect x="0"  y="8"  width="4" height="6" rx="1" fill={color} />
      <rect x="7"  y="4"  width="4" height="10" rx="1" fill={med ? color : dim} />
      <rect x="14" y="0"  width="4" height="14" rx="1" fill={high ? color : dim} />
    </svg>
  );
}

// ── Grouping ──────────────────────────────────────────────────────────────────

export interface DetectionGroup {
  key: string;
  locationCode: string;
  lead: Detection;
  detections: Detection[];
}

function groupDetections(detections: Detection[]): DetectionGroup[] {
  const map = new Map<string, Detection[]>();
  const singletons: Detection[] = [];

  for (const d of detections) {
    const { hijriMonth, hijriYear } = d.extracted;
    if (!hijriMonth) {
      singletons.push(d);
      continue;
    }
    const key = `${d.locationCode}|${hijriMonth}|${hijriYear ?? 'unknown'}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(d);
  }

  const groups: DetectionGroup[] = [];

  for (const [key, dets] of map) {
    const sorted = [...dets].sort(
      (a, b) => (CONFIDENCE_RANK[b.extracted.confidence] ?? 0) - (CONFIDENCE_RANK[a.extracted.confidence] ?? 0)
    );
    groups.push({ key, locationCode: dets[0].locationCode, lead: sorted[0], detections: sorted });
  }

  for (const d of singletons) {
    groups.push({ key: d.id, locationCode: d.locationCode, lead: d, detections: [d] });
  }

  return groups.sort(
    (a, b) => new Date(b.lead.detectedAt).getTime() - new Date(a.lead.detectedAt).getTime()
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

interface Props {
  allLocations: AdminLocationInfo[];
  highlightId?: string;
  onViewRun?: (runId: string) => void;
}

type FilterStatus = 'PENDING' | 'ALL';

export default function DetectionsPage({ allLocations, highlightId, onViewRun }: Props) {
  const [detections, setDetections] = useState<Detection[]>([]);
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('PENDING');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scraperMsg, setScraperMsg] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [confirmGroup, setConfirmGroup] = useState<DetectionGroup | null>(null);
  const [dismissingKey, setDismissingKey] = useState<string | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const { overviews } = useAllCountries(allLocations);

  const loadStatus = useCallback(async () => {
    try { setScraperStatus(await getScraperStatus()); } catch { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status: DetectionStatus | undefined = filter === 'ALL' ? undefined : 'PENDING';
      const [dets] = await Promise.all([getDetections(status), loadStatus()]);
      setDetections(dets);
    } catch { setError('Failed to load inbox'); }
    finally { setLoading(false); }
  }, [filter, loadStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading]);

  useEffect(() => {
    if (!scraperStatus?.isRunning) return;
    const id = setInterval(loadStatus, 5000);
    return () => clearInterval(id);
  }, [scraperStatus?.isRunning, loadStatus]);

  async function dismissGroup(group: DetectionGroup) {
    setDismissingKey(group.key);
    try {
      const updated = await Promise.all(
        group.detections.map((d) => updateDetectionStatus(d.id, 'DISMISSED'))
      );
      const updatedById = Object.fromEntries(updated.map((d) => [d.id, d]));
      setDetections((prev) => prev.map((d) => updatedById[d.id] ?? d));
    } catch { setError('Failed to dismiss'); }
    finally { setDismissingKey(null); }
  }

  async function runScraper(force: boolean) {
    setScraperMsg(force ? 'Running now (forced)…' : 'Starting…');
    try {
      await triggerScraper(force);
      setTimeout(loadStatus, 2000);
      setTimeout(() => setScraperMsg(null), 4000);
    } catch { setScraperMsg('Failed to start'); setTimeout(() => setScraperMsg(null), 3000); }
  }

  function handleConfirmSuccess() {
    setConfirmGroup(null);
    load();
  }

  const groups = groupDetections(detections);
  const pendingCount = detections.filter((d) => d.status === 'PENDING').length;

  const nextWindow = overviews
    .filter((o) => o.daysToWindow !== null)
    .sort((a, b) => (a.daysToWindow ?? 99) - (b.daysToWindow ?? 99))[0];

  // Find the group that contains the highlighted detection
  const highlightedGroupKey = highlightId
    ? groups.find((g) => g.detections.some((d) => d.id === highlightId))?.key ?? null
    : null;

  return (
    <>
      <main className={styles.main}>
        {scraperMsg && <div className={styles.infoBar}>{scraperMsg}</div>}
        {error && <div className={styles.errorBar}>{error}</div>}

        <div className={styles.toolbar}>
          <div className={styles.filters}>
            <button className={filter === 'PENDING' ? styles.filterOn : styles.filterOff} onClick={() => setFilter('PENDING')}>
              Needs review {pendingCount > 0 && <span className={styles.filterCount}>{pendingCount}</span>}
            </button>
            <button className={filter === 'ALL' ? styles.filterOn : styles.filterOff} onClick={() => setFilter('ALL')}>
              All
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button className={styles.runBtn} onClick={() => runScraper(false)} disabled={scraperStatus?.isRunning} title="Run scraper (respects day-29 window)">
              {scraperStatus?.isRunning ? 'Running…' : 'Run now'}
            </button>
            <button className={styles.forceBtn} onClick={() => runScraper(true)} disabled={scraperStatus?.isRunning} title="Bypass day-29 guard">
              Force
            </button>
            <button className={styles.refreshBtn} onClick={load}>Refresh</button>
          </div>
        </div>

        {loading ? (
          <div className={styles.empty}><div className={styles.spinner} /></div>
        ) : groups.length === 0 ? (
          <EmptyState filter={filter} nextWindow={nextWindow ?? null} />
        ) : (
          <div className={styles.list}>
            {groups.map((group) => (
              <DetectionGroupCard
                key={group.key}
                group={group}
                highlight={group.key === highlightedGroupKey}
                highlightRef={group.key === highlightedGroupKey ? highlightRef : undefined}
                expanded={expandedKey === group.key}
                dismissing={dismissingKey === group.key}
                onToggle={() => setExpandedKey((p) => p === group.key ? null : group.key)}
                onConfirm={() => setConfirmGroup(group)}
                onDismiss={() => dismissGroup(group)}
                onViewRun={onViewRun}
              />
            ))}
          </div>
        )}
      </main>

      {confirmGroup && (
        <ConfirmDetectionModal
          group={confirmGroup}
          authority={allLocations.find((l) => l.code === confirmGroup.locationCode)?.authority ?? ''}
          onClose={() => setConfirmGroup(null)}
          onSuccess={handleConfirmSuccess}
        />
      )}
    </>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ filter, nextWindow }: { filter: FilterStatus; nextWindow: CountryOverview | null }) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>📭</div>
      <p className={styles.emptyTitle}>{filter === 'PENDING' ? 'Inbox is clear' : 'No detections yet'}</p>
      {nextWindow ? (
        <p className={styles.emptyHint}>
          Next sighting window: <strong>{nextWindow.location.name}</strong> in {nextWindow.daysToWindow} day{nextWindow.daysToWindow !== 1 ? 's' : ''}.
          The scraper will run automatically.
        </p>
      ) : (
        <p className={styles.emptyHint}>The scraper runs every 1h during the sighting window (day 28–29) and every 24h otherwise. Use "Force" to test anytime.</p>
      )}
    </div>
  );
}

// ── Detection Group Card ──────────────────────────────────────────────────────

const SOURCE_TIER: Record<string, string> = { website: 'Official', social: 'Social', news: 'News' };
const SOURCE_COLOR: Record<string, string> = { website: styles.tierOfficial, social: styles.tierSocial, news: styles.tierNews };

interface GroupCardProps {
  group: DetectionGroup;
  highlight: boolean;
  highlightRef?: React.RefObject<HTMLDivElement | null>;
  expanded: boolean;
  dismissing: boolean;
  onToggle: () => void;
  onConfirm: () => void;
  onDismiss: () => void;
  onViewRun?: (runId: string) => void;
}

function DetectionGroupCard({ group, highlight, highlightRef, expanded, dismissing, onToggle, onConfirm, onDismiss, onViewRun }: GroupCardProps) {
  const { lead, detections } = group;
  const monthName = lead.extracted.hijriMonth ? MONTH_NAMES[lead.extracted.hijriMonth] : 'Unknown month';
  const isPending = lead.status === 'PENDING';
  const extraCount = detections.length - 1;

  return (
    <div
      ref={highlightRef as React.RefObject<HTMLDivElement>}
      className={`${styles.card} ${highlight ? styles.highlighted : ''} ${!isPending ? styles.cardDone : ''}`}
    >
      {/* Clickable summary row */}
      <div
        className={styles.cardTop}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
      >
        <div className={styles.cardMeta}>
          <span className={styles.locBadge}>{lead.locationCode}</span>
          <span className={styles.sourceName}>{lead.source.name}</span>
          {extraCount > 0 && (
            <span className={styles.sourceCount}>+{extraCount} source{extraCount > 1 ? 's' : ''}</span>
          )}
          <span className={`${styles.tier} ${SOURCE_COLOR[lead.source.type] ?? ''}`}>
            {SOURCE_TIER[lead.source.type] ?? lead.source.type}
          </span>
          {!isPending && (
            <span className={lead.status === 'CONFIRMED' ? styles.statusConfirmed : styles.statusDismissed}>
              {lead.status === 'CONFIRMED' ? 'Confirmed' : 'Dismissed'}
            </span>
          )}
          <span className={styles.time}>{formatRelative(lead.detectedAt)}</span>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.cardConf}>
            <SignalIcon confidence={lead.extracted.confidence} />
            <span className={styles.confLabel}>{lead.extracted.confidence}</span>
          </div>
          <div className={styles.cardSummary}>
            <span className={styles.monthName}>
              {monthName}{lead.extracted.hijriYear ? ` ${lead.extracted.hijriYear}` : ''}
            </span>
            {lead.extracted.startDate && (
              <span className={styles.startDate}>starts {lead.extracted.startDate}</span>
            )}
          </div>
          <p className={styles.reasoning}>{lead.extracted.reasoning}</p>
        </div>
      </div>

      {/* Expanded: all sources */}
      {expanded && (
        <div className={styles.cardExpanded}>
          {detections.map((d, i) => (
            <div key={d.id} className={styles.sourceEntry}>
              <div className={styles.sourceEntryMeta}>
                <span className={styles.sourceEntryIndex}>{i + 1}</span>
                <span className={styles.sourceEntryName}>{d.source.name}</span>
                <span className={`${styles.tier} ${SOURCE_COLOR[d.source.type] ?? ''}`}>
                  {SOURCE_TIER[d.source.type] ?? d.source.type}
                </span>
                <SignalIcon confidence={d.extracted.confidence} />
                <span className={styles.confLabel}>{d.extracted.confidence}</span>
              </div>
              <p className={styles.reasoning} style={{ marginTop: 4 }}>{d.extracted.reasoning}</p>
              <pre className={styles.rawContent}>{d.rawContent}</pre>
              <div className={styles.expandedLinks}>
                <a href={d.source.url} target="_blank" rel="noreferrer" className={styles.sourceLink}>
                  View source →
                </a>
                {d.pipelineRunId && onViewRun && (
                  <button className={styles.runLink} onClick={() => onViewRun(d.pipelineRunId!)}>
                    View pipeline run →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions — only for pending */}
      {isPending && (
        <div className={styles.cardActions}>
          <button className={styles.confirmBtn} onClick={onConfirm}>
            Confirm month
          </button>
          <button className={styles.dismissBtn} onClick={onDismiss} disabled={dismissing}>
            {dismissing ? 'Dismissing…' : detections.length > 1 ? 'Dismiss all' : 'Dismiss'}
          </button>
        </div>
      )}
    </div>
  );
}
